// Автоподбор качества графики под устройство.
//
// Цель простая: чтобы мир открывался и на слабом ноутбуке, и на телефоне, а
// не только на игровой машине. Класс устройства определяется один раз при
// входе по тому, что реально доступно в браузере: рендерер GPU, число ядер,
// объём памяти, тип ввода и плотность экрана. Игрок в любой момент может
// переключить профиль руками — авто только предлагает старт.

export const TIERS = ['low', 'medium', 'high', 'ultra']

// Что каждый профиль делает с настройками движка.
export const TIER_PROFILES = {
  low: { dpr: 0.75, shadows: 'none', postprocessing: false, bloom: false, ao: false },
  // Средний профиль включает композер РАДИ фирменного вида (контур + грейдинг):
  // это один полноэкранный проход, дешевле теней, а без него мир на большинстве
  // машин выглядел «серыми коробками». Дорогие блум и AO остаются выключены.
  medium: { dpr: 1, shadows: 'low', postprocessing: true, bloom: false, ao: false },
  high: { dpr: 1, shadows: 'med', postprocessing: true, bloom: true, ao: false },
  ultra: { dpr: 1.5, shadows: 'high', postprocessing: true, bloom: true, ao: true },
}

export const TIER_LABELS = {
  low: 'ЛЁГКИЙ',
  medium: 'СРЕДНИЙ',
  high: 'ВЫСОКИЙ',
  ultra: 'МАКСИМУМ',
}

// Слабые интегрированные и мобильные GPU: им сразу лёгкий профиль.
// Интеловские встройки (HD/UHD/Iris) и Radeon(TM) Graphics ноутбучных Ryzen
// попадают сюда целиком: на проде именно они «получали высокий» и лагали.
const WEAK_GPU = /(swiftshader|llvmpipe|software|mali|adreno \(tm\) [1-5]|adreno [1-5]|powervr|intel\(r\) hd graphics|intel\(r\) uhd graphics|iris|radeon\(tm\) graphics)/i
// Явно мощные дискретные: им можно максимум. GTX 16xx сюда не входит —
// это ноутбучный середняк, ему высокий только руками.
const STRONG_GPU = /(rtx|radeon rx [5-9]|geforce (gtx )?(20|30|40|50)[5-9]|apple m[1-9]|arc a[3-7])/i

export function readGpu(canvasFactory) {
  try {
    const canvas = canvasFactory ? canvasFactory() : document.createElement('canvas')
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl')
    if (!gl) return { renderer: '', webgl2: false }
    const debug = gl.getExtension('WEBGL_debug_renderer_info')
    const renderer = debug ? String(gl.getParameter(debug.UNMASKED_RENDERER_WEBGL) || '') : String(gl.getParameter(gl.RENDERER) || '')
    return { renderer, webgl2: typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext }
  } catch (err) {
    return { renderer: '', webgl2: false }
  }
}

// Чистая функция: по фактам об устройстве возвращает профиль и объяснение,
// почему именно он. Объяснение показываем игроку — пусть видит, что решили.
export function pickTier({ renderer = '', webgl2 = true, cores = 4, memoryGb = 4, touch = false, dpr = 1, narrow = false } = {}) {
  const gpu = String(renderer).toLowerCase()
  if (!webgl2) return { tier: 'low', reason: 'без WebGL2 — только лёгкий профиль' }
  if (WEAK_GPU.test(gpu)) return { tier: 'low', reason: `слабый графический чип (${renderer || 'неизвестен'})` }
  if (touch || narrow) {
    const strongMobile = /apple m[1-9]|apple a1[4-9]|adreno \(tm\) 7|adreno 7/i.test(gpu)
    return strongMobile
      ? { tier: 'medium', reason: 'телефон с мощным чипом' }
      : { tier: 'low', reason: 'мобильное устройство — бережём батарею и кадры' }
  }
  if (cores <= 2 || memoryGb <= 2) return { tier: 'low', reason: `мало ресурсов (${cores} ядра, ${memoryGb} ГБ)` }
  if (STRONG_GPU.test(gpu) && cores >= 8 && memoryGb >= 8) {
    return { tier: dpr >= 2 ? 'ultra' : 'high', reason: `мощная видеокарта (${renderer})` }
  }
  // Высокий профиль — только опознанно мощным картам: он включает
  // пост-обработку, а именно на неопознанных ноутбучных чипах она и лагает.
  // Кто уверен в железе — включит ВЫСОКИЙ руками в настройках.
  if (cores >= 4 && memoryGb >= 4) return { tier: 'medium', reason: `карта не опознана как мощная (${renderer || 'нет данных'}) — средний профиль` }
  return { tier: 'low', reason: 'устройство не опознано — начинаем с лёгкого' }
}

// Собирает факты об этом устройстве.
export function detectDevice() {
  const gpu = readGpu()
  const nav = typeof navigator !== 'undefined' ? navigator : {}
  return {
    renderer: gpu.renderer,
    webgl2: gpu.webgl2,
    cores: Number(nav.hardwareConcurrency) || 4,
    memoryGb: Number(nav.deviceMemory) || 4,
    touch: typeof window !== 'undefined' && (('ontouchstart' in window) || Number(nav.maxTouchPoints) > 0),
    dpr: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
    narrow: typeof window !== 'undefined' ? window.innerWidth < 900 : false,
  }
}

// Применяет профиль к настройкам движка.
export function applyTier(world, tier) {
  const profile = TIER_PROFILES[tier] || TIER_PROFILES.medium
  const prefs = world?.prefs
  if (!prefs) return profile
  prefs.setDPR?.(profile.dpr)
  prefs.setShadows?.(profile.shadows)
  prefs.setPostprocessing?.(profile.postprocessing)
  prefs.setBloom?.(profile.bloom)
  prefs.setAO?.(profile.ao)
  return profile
}

// Тонмаппинг: цепочка пост-обработки заканчивается собственным ACES-эффектом,
// поэтому при включённом композере рендереру тонмапить нельзя — картинка
// прожмётся дважды. А прямой путь (лёгкий и средний профили) и VR обязаны
// тонмапить сами, иначе слабые устройства видят сырые пересветы вместо той же
// картинки, что и мощные.
export function directToneMapping({ postprocessing = false, xr = false } = {}) {
  return postprocessing && !xr ? 'none' : 'aces'
}

const STORAGE_KEY = 'testcs.graphics.tier'

// Один раз на устройство: если игрок уже выбирал профиль — уважаем выбор.
export function ensureGraphicsTier(world, storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  const saved = storage?.getItem?.(STORAGE_KEY)
  if (saved && TIERS.includes(saved)) {
    applyTier(world, saved)
    return { tier: saved, reason: 'выбрано вручную', auto: false }
  }
  const device = detectDevice()
  const { tier, reason } = pickTier(device)
  applyTier(world, tier)
  return { tier, reason, auto: true, device }
}

export function setGraphicsTier(world, tier, storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  if (!TIERS.includes(tier)) return null
  storage?.setItem?.(STORAGE_KEY, tier)
  applyTier(world, tier)
  return tier
}
